import React, { Fragment } from 'react';
import { Field } from 'formik';
import { Label, Col, Row, FormGroup, CustomInput } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import Select, { Creatable } from 'react-select';

import { ErrorMessage, RequireRole, Spinner } from '../../';

const formatGroupLabel = data => (
  <FormattedMessage id={data.label} />
);

const FieldBuilder = ({
  formFieldData,
  errors,
  touched,
  locked = false,
  userRoles = [],
  isSubmitting,
}) => formFieldData.map(({ key, type, data }) => {
  let component;
  const {
    rowClasses,
    colClasses,
  } = data;
  switch (type) {
    case 'component': {
      const { component: fieldComponent } = data;
      return fieldComponent;
    }
    case 'section': {
      const {
        title: sectionTitle,
        renderSectionBreak = true,
        actionComponent,
      } = data;
      component = (
        <Fragment>
          {renderSectionBreak && <hr />}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="m-0">
              <Label className="font-weight-light m-0"><FormattedMessage id={sectionTitle} /></Label>
            </h5>
            {actionComponent}
          </div>
        </Fragment>
      );
      break;
    }
    case 'select': {
      const {
        name: fieldName,
        defaultOptionValue,
        defaultOptionLabel,
        label,
        isLocked = false,
      } = data;
      component = (
        <FormGroup>
          <Label for={fieldName}><FormattedMessage id={label} /></Label>
          <Field
            name={fieldName}
            render={() => (
              <Select
                name={fieldName}
                classNamePrefix="react-select-container"
                defaultValue={{
                  value: defaultOptionValue,
                  label: defaultOptionLabel,
                }}
                className={
                  errors[fieldName] && touched[fieldName]
                    ? 'form-control is-invalid p-0'
                    : 'react-select-container form-control p-0 mb-0 border-0'
                }
                isDisabled={isLocked || locked}
                components={{ LoadingIndicator: Spinner }}
              />
            )}
          />
          <ErrorMessage errorText={errors[fieldName]} />
        </FormGroup>
      );
      break;
    }
    case 'creatable': {
      const {
        name: fieldName,
        dataCy,
        label,
        defaultOptionValue,
        defaultOptionLabel,
        options,
        placeholder,
        maxLength,
        customStyles,
        onScrollToBottom,
        isComponentLoading,
        isLocked = false,
      } = data;
      component = (
        <FormGroup data-cy={dataCy}>
          <Label for={fieldName}><FormattedMessage id={label} /></Label>
          <Field
            name={fieldName}
            render={({ form }) => (
              <Creatable
                name={fieldName}
                options={options}
                defaultValue={defaultOptionValue && defaultOptionLabel ? {
                  value: defaultOptionValue,
                  label: defaultOptionLabel,
                } : undefined}
                formatGroupLabel={formatGroupLabel}
                placeholder={placeholder}
                classNamePrefix="react-select-container"
                className={
                  errors[fieldName] && touched[fieldName]
                    ? 'form-control is-invalid p-0'
                    : 'react-select-container form-control p-0 mb-0 border-0'
                }
                onBlur={() => form.setFieldTouched(fieldName, true, true)}
                onChange={option => form.setFieldValue(fieldName, option.value)}
                onInputChange={input => input.substr(0, maxLength)}
                isLoading={isComponentLoading}
                styles={customStyles}
                onMenuScrollToBottom={onScrollToBottom}
                isDisabled={isLocked || locked || isSubmitting}
              />
            )}
          />
          <ErrorMessage errorText={errors[fieldName]} />
        </FormGroup>
      );
      break;
    }
    case 'input': {
      const {
        name: fieldName,
        label,
        placeholder,
        isLocked = false,
      } = data;
      component = (
        <FormGroup>
          <Label for={fieldName}><FormattedMessage id={label} /></Label>
          <Field
            name={fieldName}
            type="text"
            className={errors[fieldName] && touched[fieldName] ? 'form-control is-invalid' : 'form-control'}
            placeholder={placeholder}
            disabled={isLocked || locked || isSubmitting}
          />
          <ErrorMessage errorText={errors[fieldName]} />
        </FormGroup>
      );
      break;
    }
    case 'switch': {
      const {
        name: fieldName,
        id: fieldId,
        label,
        isLocked = false,
        onChange = () => {},
      } = data;
      component = (
        <FormGroup className="mh-0">
          <Field
            name={fieldName}
            render={({ form }) => (
              <CustomInput
                type="switch"
                id={fieldId}
                label={label}
                disabled={isLocked || locked}
                onChange={(e) => { form.setFieldValue(`${fieldName}`, e.target.checked); onChange(e, form); }}
                checked={form.values[fieldName]}
              />
            )}
          />
        </FormGroup>
      );
      break;
    }
    case 'collection': {
      const {
        formFields,
      } = data;
      component = (
        <FieldBuilder
          formFieldData={formFields}
          errors={errors}
          touched={touched}
          locked={locked}
          isSubmitting={isSubmitting}
        />
      );
      break;
    }
    default:
  }
  const { requiredRole = '' } = data;
  return (
    requiredRole ?
      (
        <RequireRole
          key={key}
          child={(
            <Row className={rowClasses && rowClasses.join(' ')}>
              <Col className={colClasses && colClasses.join(' ')}>
                {component}
              </Col>
            </Row>
          )}
          userRoles={userRoles}
          requiredRole={requiredRole}
        />
      ) :
      (
        <Row key={key} className={rowClasses && rowClasses.join(' ')}>
          <Col className={colClasses && colClasses.join(' ')}>
            {component}
          </Col>
        </Row>
      )
  );
});

export default FieldBuilder;



// WEBPACK FOOTER //
// ./src/components/Forms/FieldBuilder/FieldBuilder.jsx